'use client';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef } from 'react';
import { useAuthSync } from '../../../../auth/oauth/auth-sync.js';
import { isLoginMethodAllowed } from '../../../../auth/oauth/login-collision.js';
import { useAuth } from '../../../../auth/state/use-auth.js';
import { useRegisterWallet } from '../../../../generated/mutations/useRegisterWallet.gen.js';
import { useTurnkeySession } from '../../../../generated/mutations/useTurnkeySession.gen.js';
import { readLoginApiError } from '../../../shared/auth/session/login-error.js';
import { loginFailureMessage } from '../../../shared/auth/session/resolve-login.js';
import { useSdkTurnkey } from '../turnkey/use-sdk-turnkey.js';
import { fetchWallets, invalidateWalletCache, WALLETS_QUERY_KEY } from '../wallets/use-sdk-wallets.js';
import { findWalletsInAnotherSubOrg } from './reconcile-helpers.js';
// ── Helpers ──────────────────────────────────────────────────────────────────
const isDev = process.env.NODE_ENV === 'development';
// Per-call ceilings for the backend round-trips in the login completion. None
// of these blocks is allowed to hang the user on the `/auth/callback` spinner:
// the critical session-stamp gets the most headroom; the two best-effort reads
// fail fast and fall back. The completion watchdog (`SYNC_WATCHDOG_MS`) is the
// backstop for the case where the sync never *starts* (Turnkey org/wallets
// never arrive), and must exceed the worst-case sum of these.
const CHECK_LOGIN_METHOD_TIMEOUT_MS = 6_000;
const SESSION_STAMP_TIMEOUT_MS = 12_000;
/** Extra wait on the ORIGINAL in-flight stamp after its ceiling trips, before
 *  firing a second mutation — withTimeout cannot cancel the request, so the
 *  grace avoids two concurrent create-or-find stamps for the common
 *  slow-but-succeeding case. */
const STAMP_RETRY_GRACE_MS = 6_000;
const FETCH_ME_TIMEOUT_MS = 6_000;
const SYNC_WATCHDOG_MS = 30_000;
/** Error thrown when a bounded auth call exceeds its timeout. */
class AuthTimeoutError extends Error {
  constructor(label, ms) {
    super(`${label} timed out after ${String(ms)}ms`);
    this.name = 'AuthTimeoutError';
  }
}
/**
 * Race `promise` against a timeout. On timeout the returned promise rejects
 * with `AuthTimeoutError` so the caller's catch can surface a retryable error
 * instead of awaiting forever. The timer is always cleared (no dangling timer
 * if `promise` settles first).
 */
function withTimeout(promise, ms, label) {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      reject(new AuthTimeoutError(label, ms));
    }, ms);
    promise.then(value => {
      clearTimeout(timer);
      resolve(value);
    }, err => {
      clearTimeout(timer);
      reject(err instanceof Error ? err : new Error(String(err)));
    });
  });
}
/** Map the Turnkey SDK method string to a canonical auth method string. */
function toAuthMethod(turnkeyMethod) {
  switch (turnkeyMethod) {
    case 'otp':
      return 'otp';
    case 'oauth':
      return 'oauth';
    case 'wallet':
      return 'wallet';
    case 'passkey':
      return 'passkey';
    default:
      return 'otp';
  }
}
function turnkeyWalletType(source) {
  if (source === 'connected') return 'external';
  if (source === 'imported') return 'imported';
  return 'embedded';
}
function extractTurnkeyWallets(turnkeyWallets, activeOrgId) {
  /* v8 ignore start -- defensive: both callers return on empty activeOrgId before calling */
  if (activeOrgId === undefined || activeOrgId === '') return [];
  /* v8 ignore stop */
  return turnkeyWallets.flatMap(w => {
    const walletType = turnkeyWalletType(w.source);
    if (walletType === 'external') {
      return w.accounts.map(account => ({
        walletType: 'external',
        address: account.address
      }));
    }
    return w.accounts.filter(account => account.organizationId === activeOrgId).map(account => ({
      walletType,
      walletId: w.walletId,
      address: account.address
    }));
  });
}
function selectEmbeddedForSignup(wallets) {
  return wallets.flatMap(w => w.walletType === 'embedded' ? [{
    walletId: w.walletId,
    address: w.address
  }] : []);
}
function defaultLabelFor(walletType) {
  if (walletType === 'imported') return 'Imported Wallet';
  if (walletType === 'external') return 'External Wallet';
  return 'Wallet';
}
// ── Wallet reconcile ─────────────────────────────────────────────────────────
async function reconcileWalletsWithBackend(turnkeyWallets, activeSubOrgId, accountSubOrgId, queryClient, registerWalletAsync, onDiagnostic) {
  // A Turnkey session whose sub-org is not the one the account points at has no
  // authority over the wallet list: its wallets belong to a different sub-org,
  // and the account's own wallets would look unreachable here.
  if (accountSubOrgId !== null && accountSubOrgId !== activeSubOrgId) {
    onDiagnostic?.('wallet_reconcile_skipped_foreign_sub_org', {
      sessionSubOrg: activeSubOrgId,
      accountSubOrg: accountSubOrgId
    });
    return {
      registered: 0,
      unreachable: 0
    };
  }
  // The decision below is "which Turnkey wallets does the backend not know",
  // so it must read the backend, not the persisted copy of it. The wallets
  // query is persisted and fresh for five minutes; after an archive the copy
  // holds the optimistic list, and if the tab closed before the restore was
  // flushed, the next load would re-register a wallet the backend never lost
  // and keep showing the stale list. Fetching fresh here also updates the
  // cache the panel renders from.
  const backendWallets = await queryClient.fetchQuery({
    queryKey: WALLETS_QUERY_KEY,
    queryFn: ({
      client
    }) => fetchWallets(client),
    staleTime: 0
  });
  const backendAddresses = new Set(backendWallets.map(w => w.address));
  // ── ADD missing wallets ─────────────────────────────────────────────────────
  const missing = turnkeyWallets.filter(tw => !backendAddresses.has(tw.address));
  let registered = 0;
  let crossUserConflicts = 0;
  if (missing.length > 0 && isDev) {
    console.warn(`[sdk.reconcile] ${String(missing.length)} wallet(s) missing from backend`);
  }
  for (const tw of missing) {
    try {
      if (tw.walletType === 'external') {
        await registerWalletAsync({
          publicKey: tw.address,
          walletType: 'external',
          label: defaultLabelFor('external')
        });
      } else {
        await registerWalletAsync({
          turnkeyWalletId: tw.walletId,
          turnkeySubOrgId: activeSubOrgId,
          publicKey: tw.address,
          walletType: tw.walletType,
          label: defaultLabelFor(tw.walletType)
        });
      }
      registered++;
    } catch (err) {
      const errMsg = err instanceof Error ? err.message : String(err);
      if (errMsg.includes('WALLET_ALREADY_LINKED') || errMsg.includes('already linked')) {
        crossUserConflicts++;
      } else if (errMsg.includes('SUB_ORG_MISMATCH') || errMsg.includes('sub-org mismatch')) {
        if (isDev) {
          console.warn('[sdk.reconcile] sub-org mismatch', {
            publicKey: tw.address,
            walletType: tw.walletType
          });
        }
      }
    }
  }
  if (crossUserConflicts > 0 && isDev) {
    console.warn(`[sdk.reconcile] ${String(crossUserConflicts)} wallet(s) belong to a different user — skipped`);
  }
  // ── REPORT wallets this session cannot reach ────────────────────────────────
  // A wallet stamped with another sub-org is NOT dead: its key lives in that
  // sub-org and the balance with it, reachable by signing in with the method
  // that lands there. Archiving hid it from its owner, and re-registering
  // cannot bring it back while the account points elsewhere (register refuses a
  // foreign stamp), so the list is left alone and the count is reported.
  const unreachable = findWalletsInAnotherSubOrg(backendWallets, activeSubOrgId);
  if (unreachable.length > 0) {
    onDiagnostic?.('wallet_unreachable_sub_org', {
      count: unreachable.length,
      sessionSubOrg: activeSubOrgId,
      accountSubOrg: accountSubOrgId ?? ''
    });
  }
  if (isDev && (registered > 0 || unreachable.length > 0)) {
    console.warn(`[sdk.reconcile] registered=${String(registered)} unreachable=${String(unreachable.length)}`);
  }
  if (registered > 0) {
    // The list the panel renders predates the rows just added.
    await invalidateWalletCache(queryClient);
  }
  return {
    registered,
    unreachable: unreachable.length
  };
}
async function checkLoginMethod(params, signal) {
  // The standalone `/v1/auth/check-login-method` route was folded into the
  // unified `/v1/auth/identity-check?kind=login-method` endpoint — the old
  // path 404s. The `kind=login-method` response carries the same
  // `signupMethod` field this helper consumes.
  const qs = new URLSearchParams({
    kind: 'login-method'
  });
  if (params.email) qs.set('email', params.email);
  if (params.provider) qs.set('provider', params.provider);
  if (params.providerUserId) qs.set('providerUserId', params.providerUserId);
  const url = `/v1/auth/identity-check?${qs.toString()}`;
  const res = await fetch(url, {
    credentials: 'include',
    signal
  });
  if (!res.ok) throw new Error(`GET /v1/auth/identity-check failed: ${res.status}`);
  const json = await res.json();
  const raw = json !== null && typeof json === 'object' && 'data' in json ? json.data : json;
  if (raw === null || typeof raw !== 'object') return {
    signupMethod: null,
    connectedProviders: [],
    emailLinkedProvider: null
  };
  const r = raw;
  const connectedProviders = Array.isArray(r.connectedProviders) ? r.connectedProviders.filter(p => typeof p === 'string') : [];
  return {
    signupMethod: typeof r.signupMethod === 'string' ? r.signupMethod : null,
    connectedProviders,
    emailLinkedProvider: typeof r.emailLinkedProvider === 'string' ? r.emailLinkedProvider : null
  };
}
// ── fetchMeProfile (via bare fetch) ──────────────────────────────────────────
async function fetchMeProfileForLogin(signal) {
  try {
    const res = await fetch('/v1/me', {
      credentials: 'include',
      signal
    });
    if (res.status === 401 || res.status === 403) return null;
    if (!res.ok) return null;
    const json = await res.json();
    const raw = json !== null && typeof json === 'object' && 'data' in json ? json.data : json;
    if (raw === null || typeof raw !== 'object') return null;
    const p = raw;
    return {
      id: typeof p.id === 'string' ? p.id : '',
      email: typeof p.email === 'string' ? p.email : '',
      phoneNumber: typeof p.phoneNumber === 'string' ? p.phoneNumber : null,
      displayName: typeof p.displayName === 'string' ? p.displayName : null,
      handle: typeof p.handle === 'string' ? p.handle : null,
      avatarUrl: typeof p.avatarUrl === 'string' ? p.avatarUrl : null,
      avatarNsfw: typeof p.avatarNsfw === 'boolean' ? p.avatarNsfw : null,
      avatarPreferences: p.avatarPreferences !== null && typeof p.avatarPreferences === 'object' && typeof p.avatarPreferences.rasterise === 'boolean' ? {
        rasterise: p.avatarPreferences.rasterise
      } : null,
      heroUrl: typeof p.heroUrl === 'string' ? p.heroUrl : null,
      coverNsfw: typeof p.heroNsfw === 'boolean' ? p.heroNsfw : null,
      bio: typeof p.bio === 'string' ? p.bio : null,
      country: typeof p.country === 'string' ? p.country : null,
      locationSource: typeof p.locationSource === 'string' ? p.locationSource : null,
      createdAt: typeof p.createdAt === 'string' ? p.createdAt : new Date().toISOString(),
      onboardingCompleted: p.onboardingCompleted === true,
      connectedProviders: Array.isArray(p.connectedProviders) ? p.connectedProviders.map(cp => ({
        provider: typeof cp.provider === 'string' ? cp.provider : '',
        ...(typeof cp.username === 'string' ? {
          username: cp.username
        } : {}),
        ...(typeof cp.displayName === 'string' ? {
          displayName: cp.displayName
        } : {})
      })) : [],
      turnkeySubOrgId: typeof p.turnkeySubOrgId === 'string' ? p.turnkeySubOrgId : null
    };
  } catch {
    return null;
  }
}
// ── Component ─────────────────────────────────────────────────────────────────
/**
 * Side-effect-only component that bridges Turnkey auth to the Morfi backend.
 *
 * Renders null. Mount inside the TurnkeyProvider + QueryClientProvider trees.
 *
 * App-specific side-effects (toast, routing, analytics, onboarding reset) are
 * injected via the `callbacks` prop so this component has zero frontend deps.
 */
export function SdkAuthSuccessHandler({
  callbacks
}) {
  const {
    user,
    wallets,
    walletProviders,
    session
  } = useSdkTurnkey();
  const {
    setUser,
    isAuthenticated,
    user: morfiUser
  } = useAuth();
  const {
    peekFreshLogin,
    consumeFreshLogin,
    freshLoginVersion,
    subscribeOauthLoginStarted,
    subscribeOauthLoginFailed
  } = useAuthSync();
  const queryClient = useQueryClient();
  const turnkeySession = useTurnkeySession({
    meta: {
      skipGlobalErrorHandler: true
    }
  });
  const registerWalletMut = useRegisterWallet();
  const activeOrgId = session?.organizationId;
  const syncInProgressRef = useRef(false);
  const loginControllerRef = useRef(null);
  const mountedRef = useRef(false);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      // StrictMode immediately reactivates this mounted bridge. Only a real
      // unmount should cancel the consumed login and its pending requests.
      queueMicrotask(() => {
        if (!mountedRef.current) loginControllerRef.current?.abort();
      });
    };
  }, []);
  const reconcileRunRef = useRef(false);
  const reportDiagnostic = useCallback((name, payload) => {
    callbacksRef.current?.onEvent?.(name, payload);
  }, []);
  // Keep callbacks in a ref so effects always call the latest version. The
  // ref is synced in an effect — never written during render (a
  // React-correctness violation the React Compiler rejects).
  const callbacksRef = useRef(callbacks);
  useEffect(() => {
    callbacksRef.current = callbacks;
  });
  // ── WALLET RECONCILIATION ──────────────────────────────────────────────────
  useEffect(() => {
    if (!isAuthenticated || wallets.length === 0 || reconcileRunRef.current) return;
    if (syncInProgressRef.current) return;
    if (activeOrgId === undefined || activeOrgId === '') return;
    // The run guard stays unset while this session sits on another sub-org, so
    // the profile update that brings the account's own sub-org still gets a run.
    const accountSubOrgId = morfiUser?.turnkeySubOrgId ?? null;
    if (accountSubOrgId !== null && accountSubOrgId !== activeOrgId) {
      reportDiagnostic('wallet_reconcile_skipped_foreign_sub_org', {
        sessionSubOrg: activeOrgId,
        accountSubOrg: accountSubOrgId
      });
      return;
    }
    reconcileRunRef.current = true;
    const allTurnkeyWallets = extractTurnkeyWallets(wallets, activeOrgId);
    const subOrgId = activeOrgId;
    reconcileWalletsWithBackend(allTurnkeyWallets, subOrgId, accountSubOrgId, queryClient, registerWalletMut.mutateAsync, reportDiagnostic).catch(() => {
      reconcileRunRef.current = false;
    });
  }, [isAuthenticated, wallets, activeOrgId, morfiUser?.turnkeySubOrgId, queryClient, registerWalletMut.mutateAsync, reportDiagnostic]);
  // ── EARLY "SIGNING IN" FEEDBACK ─────────────────────────────────────────────
  // Show the signing-in toast the instant an OAuth login return is detected
  // (before the token exchange completes), so returning from the provider window
  // gives immediate feedback. Subscribe to the ref-based signal — it fires
  // mid-OAuth, so it MUST NOT go through provider state (that would re-render
  // WalletKitProvider and stall the token exchange). Idempotent with the
  // fresh-login effect's own toast call (same toast id).
  useEffect(() => {
    return subscribeOauthLoginStarted(() => {
      callbacksRef.current?.onShowSigningInToast?.();
    });
  }, [subscribeOauthLoginStarted]);
  // Token exchange failed before a session could be minted — clear the optimistic
  // signing-in toast and surface an error so the user can retry (and doesn't sit
  // on a stuck spinner).
  useEffect(() => {
    return subscribeOauthLoginFailed(failure => {
      callbacksRef.current?.onDismissSigningInToast?.();
      callbacksRef.current?.onShowErrorToast?.(loginFailureMessage(failure, callbacksRef.current?.toastMessages));
    });
  }, [subscribeOauthLoginFailed]);
  // ── COMPLETION WATCHDOG ─────────────────────────────────────────────────────
  // The FRESH LOGIN SYNC effect below early-returns (without consuming the
  // buffered login) while it waits for Turnkey to populate `session.organizationId`
  // and `wallets`. Normally those arrive within a render or two and the effect
  // re-runs to completion. But if they NEVER arrive, the sync never starts and
  // the user is stranded on the `/auth/callback` spinner forever.
  //
  // This watchdog bounds that wait. Armed on every fresh login (`freshLoginVersion`
  // bumps in `setFreshLogin`), it fires once after the ceiling. The per-call
  // timeouts own the case where the sync STARTED but a backend call hangs
  // (`syncInProgressRef`), and a consumed login / verified session means we're
  // done — so it only recovers the genuine PRE-sync strand: login still buffered,
  // sync never started, not authenticated. Recovery clears the optimistic toast,
  // surfaces a retryable error, and emits telemetry so hangs are observable.
  useEffect(() => {
    if (freshLoginVersion === 0 || peekFreshLogin() === null) return;
    const timer = setTimeout(() => {
      if (peekFreshLogin() === null || syncInProgressRef.current || isAuthenticated) return;
      consumeFreshLogin();
      callbacksRef.current?.onDismissSigningInToast?.();
      callbacksRef.current?.onEvent?.('auth_error', {
        error: 'completion_watchdog_timeout',
        method: null
      });
      callbacksRef.current?.onShowErrorToast?.(callbacksRef.current?.toastMessages?.error ?? 'An error occurred during sign in');
    }, SYNC_WATCHDOG_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [freshLoginVersion, peekFreshLogin, consumeFreshLogin, isAuthenticated]);
  const loginSync = useMutation({
    meta: {
      skipGlobalErrorHandler: true
    },
    networkMode: 'always',
    retry: false,
    gcTime: 0,
    mutationFn: async ({
      freshLogin,
      signal,
      user: user_0,
      wallets: wallets_0,
      walletProviders: walletProviders_0,
      activeOrgId: activeOrgId_0
    }) => {
      if (!mountedRef.current) return;
      signal.throwIfAborted();
      const sessionJwt = freshLogin.sessionToken;
      const method = toAuthMethod(freshLogin.turnkeyMethod);
      const oauthProfile = freshLogin.oauthProfile;
      const embeddedWallets = selectEmbeddedForSignup(extractTurnkeyWallets(wallets_0, activeOrgId_0));
      const isWalletAuth = method === 'wallet';
      const connectedProvider = walletProviders_0.find(p => p.connectedAddresses.length > 0);
      const firstConnectedAddress = connectedProvider?.connectedAddresses[0];
      const externalWalletAddress = isWalletAuth && firstConnectedAddress !== undefined ? firstConnectedAddress : null;
      const emailToSend = oauthProfile?.email ?? user_0?.userEmail ?? null;
      // Guard: Check if this identity already belongs to a user who signed up
      // with a different method. Block to prevent duplicate sub-orgs.
      const currentMethod = method === 'oauth' && oauthProfile?.provider ? oauthProfile.provider : method;
      try {
        const checkParams = {};
        if (emailToSend) checkParams.email = emailToSend;
        if (oauthProfile?.provider) checkParams.provider = oauthProfile.provider;
        if (oauthProfile?.xUserId) checkParams.providerUserId = oauthProfile.xUserId;
        const methodCheck = await withTimeout(checkLoginMethod(checkParams, signal), CHECK_LOGIN_METHOD_TIMEOUT_MS, 'check-login-method');
        if (!mountedRef.current) return;
        signal.throwIfAborted();
        // Decision lives in shared core — email-OTP and Google interchange
        // (Turnkey Social linking auto-links them at the sub-org level);
        // Apple and X are siloed — link them in-session via Connect.
        const isAllowedMethod = isLoginMethodAllowed(methodCheck.signupMethod, currentMethod, methodCheck.connectedProviders);
        if (!isAllowedMethod) {
          const labels = {
            google: 'Google',
            x: 'X',
            otp: 'email',
            email: 'email',
            apple: 'Apple'
          };
          // `isAllowedMethod` is false here, which (via `!methodCheck.signupMethod`)
          // guarantees `signupMethod` is a non-empty string at runtime; the `?? ''`
          // is a TS-narrowing guard only — the empty-string arm is unreachable.
          /* v8 ignore start -- TS-narrowing guard: signupMethod is always a non-empty string here */
          const signupKey = methodCheck.signupMethod ?? '';
          /* v8 ignore stop */
          // Minimal disclosure for email logins: the user proved ownership
          // of ONE email, so when a provider row owns that email name ONLY
          // that provider ('Please sign in with Google'). Otherwise fall
          // back to the full usable-method list — the signup provider plus
          // anything connected since ('Please sign in with X or Google'),
          // deduplicated by label so email/otp collapse to one entry.
          const labelSource = currentMethod === 'otp' && methodCheck.emailLinkedProvider !== null ? [methodCheck.emailLinkedProvider] : [signupKey, ...methodCheck.connectedProviders];
          const methodLabels = labelSource.filter(key => key !== '').map(key_0 => labels[key_0] ?? key_0).filter((label, index, all) => all.indexOf(label) === index);
          const signupLabel = methodLabels.join(' or ');
          callbacksRef.current?.onDismissSigningInToast?.();
          callbacksRef.current?.onShowErrorToast?.(callbacksRef.current?.toastMessages?.signedUpWithProvider(signupLabel) ?? `Please sign in with ${signupLabel}`);
          syncInProgressRef.current = false;
          return;
        }
      } catch {
        // SILENT-CATCH: best-effort — if it fails, allow login to proceed
      }
      if (!mountedRef.current) return;
      signal.throwIfAborted();
      const sessionParams = {
        turnkeySessionJwt: sessionJwt,
        email: emailToSend,
        embeddedWallets,
        externalWalletAddress,
        signupMethod: method === 'oauth' && oauthProfile?.provider ? oauthProfile.provider : method,
        ...(oauthProfile !== null ? {
          oauthProfile
        } : {}),
        ...(oauthProfile?.xUserId != null ? {
          xUserId: oauthProfile.xUserId
        } : {}),
        ...(oauthProfile?.providerUserId != null ? {
          providerUserId: oauthProfile.providerUserId
        } : {}),
        ...(oauthProfile?.oidcToken != null ? {
          oidcToken: oauthProfile.oidcToken
        } : {})
      };
      // `mutateAsync` resolves with the ALREADY-UNWRAPPED response data —
      // the SDK mutation factory strips the `{ data }` envelope before
      // `parseResponse` (see `createMutation.ts`). Do NOT read `.data`
      // again here; the resolved value IS `{ userId, isNewUser, onboardingCompleted }`.
      // A stamp TIMEOUT is the measured strand-mode of the /auth/callback
      // hang (live samples 2026-07-07: "turnkey-session stamp timed out
      // after 12000ms" → the user holds a Turnkey session but no Morfi
      // session, so every API call 401s and the login is lost). Recovery is
      // two-stage: first a GRACE re-await of the ORIGINAL in-flight request
      // (withTimeout cannot cancel it, and it often lands just past the
      // ceiling — reusing it avoids two concurrent create-or-find stamps),
      // then ONE fresh retry (the stamp is idempotent: create-or-find user +
      // mint a fresh cookie). This handler is mounted globally, so recovery
      // completes even after the callback page's 25s self-redirect.
      const stampParams = {
        ...sessionParams,
        signal
      };
      const firstStamp = turnkeySession.mutateAsync(stampParams);
      let sessionRaw;
      try {
        sessionRaw = await withTimeout(firstStamp, SESSION_STAMP_TIMEOUT_MS, 'turnkey-session stamp');
      } catch (stampErr) {
        if (!(stampErr instanceof AuthTimeoutError)) throw stampErr;
        try {
          sessionRaw = await withTimeout(firstStamp, STAMP_RETRY_GRACE_MS, 'turnkey-session stamp grace');
        } catch (graceErr) {
          if (!(graceErr instanceof AuthTimeoutError)) throw graceErr;
          sessionRaw = await withTimeout(turnkeySession.mutateAsync(stampParams), SESSION_STAMP_TIMEOUT_MS, 'turnkey-session stamp retry');
        }
      }
      if (!mountedRef.current) return;
      signal.throwIfAborted();
      const result = sessionRaw;
      // Fetch full profile — best-effort. A slow `/v1/me` must NOT strand the
      // user mid-login, so a timeout falls back to null (setUser below uses
      // the oauthProfile fields then; `/v1/me` reconciles on the next render).
      const profile = await withTimeout(fetchMeProfileForLogin(signal), FETCH_ME_TIMEOUT_MS, 'fetch /v1/me').catch(() => null);
      if (!mountedRef.current) return;
      signal.throwIfAborted();
      try {
        localStorage.setItem('morfi_user_id', result.userId);
      } catch {
        // SILENT-CATCH: localStorage may be blocked
      }
      setUser({
        id: result.userId,
        email: profile?.email ?? emailToSend ?? '',
        phoneNumber: profile?.phoneNumber ?? null,
        displayName: profile?.displayName ?? oauthProfile?.name ?? null,
        handle: profile?.handle ?? null,
        avatarUrl: profile?.avatarUrl ?? oauthProfile?.picture ?? null,
        avatarNsfw: null,
        avatarPreferences: profile?.avatarPreferences ?? null,
        heroUrl: null,
        coverNsfw: null,
        bio: null,
        country: profile?.country ?? null,
        locationSource: profile?.locationSource ?? null,
        createdAt: profile?.createdAt ?? new Date().toISOString(),
        // The stamp and `/v1/me` read the same users row, but they are two
        // independent code paths — trust EITHER saying completed so a drift
        // or partial deploy on one path can never re-onboard a returning
        // user (MOR-1784).
        onboardingCompleted: result.onboardingCompleted || profile?.onboardingCompleted === true,
        connectedProviders: profile?.connectedProviders ?? [],
        turnkeySubOrgId: profile?.turnkeySubOrgId ?? null
      });
      // React auth consumers identify after render; the signup event must
      // already belong to this account so it can join subsequent buys.
      callbacksRef.current?.onIdentifyUser?.(result.userId);
      callbacksRef.current?.onDismissSigningInToast?.();
      callbacksRef.current?.onEvent?.('auth_method_used', {
        method,
        isNewUser: result.isNewUser
      });
      if (result.isNewUser) {
        callbacksRef.current?.onShowAccountCreatedToast?.(profile?.displayName ?? oauthProfile?.name ?? null);
      } else {
        callbacksRef.current?.onShowWelcomeToast?.(profile?.displayName ?? null);
      }
      if (result.isNewUser) {
        callbacksRef.current?.onResetOnboardingStore?.();
      }
      if (result.isNewUser || !(result.onboardingCompleted || profile?.onboardingCompleted === true)) {
        // Onboarding-first default. Leave `next` in storage — it's consumed
        // when onboarding completes, so a captured deep-link still lands after.
        // `profile` is a second authoritative read of the same users row —
        // either source saying completed skips the detour (MOR-1784).
        callbacksRef.current?.onNavigate?.('/onboarding');
      } else {
        const consumed = callbacksRef.current?.onConsumeNextAfterAuth?.();
        callbacksRef.current?.onNavigate?.(consumed ?? '/markets');
      }
      // Post-login reconciliation (fire-and-forget)
      /* v8 ignore start -- defensive: effect already returns on empty activeOrgId before this point */
      if (activeOrgId_0 !== undefined && activeOrgId_0 !== '') {
        /* v8 ignore stop */
        const freshWallets = extractTurnkeyWallets(wallets_0, activeOrgId_0);
        void reconcileWalletsWithBackend(freshWallets, activeOrgId_0, profile?.turnkeySubOrgId ?? null, queryClient, registerWalletMut.mutateAsync, reportDiagnostic).catch(() => {
          // SILENT-CATCH: post-login reconcile is fire-and-forget
        });
      }
      reconcileRunRef.current = true;
    },
    onError: (err, {
      freshLogin: freshLogin_0,
      signal: signal_0
    }) => {
      if (signal_0.aborted || !mountedRef.current) return;
      const method_0 = toAuthMethod(freshLogin_0.turnkeyMethod);
      callbacksRef.current?.onDismissSigningInToast?.();
      const errMsg = err instanceof Error ? err.message.slice(0, 200) : String(err);
      callbacksRef.current?.onEvent?.('auth_error', {
        error: errMsg,
        method: method_0
      });
      // The server enforces the login-method gate itself
      // (AUTH_LOGIN_METHOD_NOT_ALLOWED); the pre-flight probe above is
      // only a faster path to the same message. Reuse that copy here so
      // a probe timeout keeps the specific rejection instead of a generic
      // error toast. The backend message ends with the label list
      // ('… sign in with X or Google') and is read off the error envelope —
      // `errMsg` is the truncated developer string and drops that tail.
      const apiError = readLoginApiError(err);
      const methodGateMatch = apiError.code === 'AUTH_LOGIN_METHOD_NOT_ALLOWED' ? (apiError.message ?? '').match(/sign in with ([^.]+)/i) : null;
      if (methodGateMatch) {
        const methodLabels_0 = methodGateMatch[1] ?? 'your original method';
        callbacksRef.current?.onShowErrorToast?.(callbacksRef.current?.toastMessages?.signedUpWithProvider(methodLabels_0) ?? `Please sign in with ${methodLabels_0}`);
        syncInProgressRef.current = false;
        return;
      }
      const walletLinkedMatch = (apiError.message ?? errMsg).match(/uses (\w+) login/);
      if (walletLinkedMatch) {
        // The regex always captures group 1 on a successful match, so `[1]` is
        // a string at runtime; the `?? 'email'` is a TS-narrowing guard only.
        /* v8 ignore start -- TS-narrowing guard: regex group 1 is always captured */
        const linkedMethod = walletLinkedMatch[1] ?? 'email';
        /* v8 ignore stop */
        const methodLabel = linkedMethod === 'google' ? 'Google' : linkedMethod === 'x' ? 'X' : linkedMethod === 'otp' ? 'email' : linkedMethod;
        callbacksRef.current?.onShowErrorToast?.(callbacksRef.current?.toastMessages?.walletAlreadyLinked(methodLabel) ?? `This wallet is linked to a ${methodLabel} account`);
      } else {
        callbacksRef.current?.onShowErrorToast?.(callbacksRef.current?.toastMessages?.error ?? 'An error occurred during sign in');
      }
    },
    onSettled: (_data, _error, {
      signal: signal_1
    }) => {
      syncInProgressRef.current = false;
      if (loginControllerRef.current?.signal === signal_1) loginControllerRef.current = null;
    }
  });
  // ── FRESH LOGIN SYNC ────────────────────────────────────────────────────────
  useEffect(() => {
    const pending = peekFreshLogin();
    if (!pending || syncInProgressRef.current) return;
    const triggerVersion = freshLoginVersion;
    void triggerVersion;
    // Skip the fresh-login sync ONLY when the SDK already holds a verified
    // authenticated session. `isAuthenticated` is the authoritative signal —
    // it reflects a confirmed `/v1/me`. It must NOT also gate on a
    // localStorage hint such as `morfi_user_id`: that value persists across
    // logout / session expiry and cannot distinguish "logged in once" from
    // "session live right now", so a stale hint silently swallows a
    // brand-new Turnkey login and the user is never signed in.
    if (isAuthenticated) {
      // Already hold a verified Morfi session. Re-running the backend sync
      // could mint a duplicate sub-org, so we don't — BUT we must not strand
      // the user on the optimistic "Signing in" toast that `onOauthRedirect`
      // raised. Dismiss it, and honour an explicit post-auth destination
      // so a returning, already
      // signed-in user who re-triggers OAuth still lands somewhere instead of
      // hanging on the spinner. No `next` (e.g. a silent session refresh) →
      // just clear the toast and do nothing else.
      consumeFreshLogin();
      callbacksRef.current?.onDismissSigningInToast?.();
      const next = callbacksRef.current?.onConsumeNextAfterAuth?.() ?? null;
      if (next !== null) {
        callbacksRef.current?.onNavigate?.(next);
      }
      return;
    }
    callbacksRef.current?.onShowSigningInToast?.();
    if (activeOrgId === undefined || activeOrgId === '') {
      return;
    }
    const allTurnkeyWallets_0 = extractTurnkeyWallets(wallets, activeOrgId);
    if (allTurnkeyWallets_0.length === 0 && wallets.length === 0) {
      return;
    }
    const freshLogin_1 = consumeFreshLogin();
    if (!freshLogin_1) {
      return;
    }
    syncInProgressRef.current = true;
    const controller = new AbortController();
    loginControllerRef.current = controller;
    loginSync.mutate({
      freshLogin: freshLogin_1,
      signal: controller.signal,
      user,
      wallets,
      walletProviders,
      activeOrgId
    });
  }, [user, wallets, walletProviders, peekFreshLogin, consumeFreshLogin, freshLoginVersion, isAuthenticated, activeOrgId, loginSync.mutate]);
  return null;
}